import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User, ApiResponse } from '../types';
import { api } from '../services/api';
import toast from 'react-hot-toast';

// Auth state interface
interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; refreshToken: string | null } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  refreshToken: null,
  loading: false,
  error: null,
  isAuthenticated: false,
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        token: action.payload.token,
  refreshToken: null,
        isAuthenticated: true,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Auth context interface
interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  refreshAuth: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set token in localStorage and axios defaults
  useEffect(() => {
    if (state.token) {
      localStorage.setItem('token', state.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`;
    } else {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    }
  }, [state.token]);

  // Set refresh token in localStorage
  // No longer store refresh tokens in localStorage; server uses HttpOnly cookie

  // Check for existing auth on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          const response = await api.get<ApiResponse<User>>('/auth/me');
          
          if (response.data.success && response.data.data) {
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                  user: response.data.data,
                  token,
                  refreshToken: null,
                },
            });
          } else {
            throw new Error('Failed to get user data');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          dispatch({ type: 'LOGOUT' });
          localStorage.removeItem('token');
        }
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const response = await api.post<ApiResponse<{
      userId: string;
      email: string;
      token: string;
    }>>('/auth/login', { email, password });

    if (response.data.success && response.data.data) {
      const token = response.data.data.token;
      
      // Set the token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch complete user data
      const userResponse = await api.get<ApiResponse<User>>('/auth/me');
      
      if (userResponse.data.success && userResponse.data.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: userResponse.data.data,
            token: token,
            refreshToken: null,
          },
        });
      } else {
        throw new Error('Failed to fetch user data');
      }
      
      toast.success('Welcome back!');
    } else {
      throw new Error(response.data.error || 'Login failed');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Login failed';
    dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
    toast.error(errorMessage);
    throw error;
  }
};

// Register function
const register = async (userData: any): Promise<void> => {
  try {
    dispatch({ type: 'AUTH_START' });
    
    const response = await api.post<ApiResponse<{
      userId: string;
      email: string;
      token: string;
    }>>('/auth/register', userData);

    if (response.data.success && response.data.data) {
      const token = response.data.data.token;
      
      // Set the token in axios headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch complete user data
      const userResponse = await api.get<ApiResponse<User>>('/auth/me');
      
      if (userResponse.data.success && userResponse.data.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: userResponse.data.data,
            token: token,
            refreshToken: null,
          },
        });
      } else {
        throw new Error('Failed to fetch user data');
      }
      
      toast.success('Account created successfully!');
    } else {
      throw new Error(response.data.error || 'Registration failed');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
    dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
    toast.error(errorMessage);
    throw error;
  }
};

// Logout function
const logout = (): void => {
  dispatch({ type: 'LOGOUT' });
  localStorage.removeItem('token');
  delete api.defaults.headers.common['Authorization'];
  toast.success('Logged out successfully');
};

// Update user function
const updateUser = (userData: Partial<User>): void => {
  if (state.user) {
    dispatch({
      type: 'UPDATE_USER',
      payload: { ...state.user, ...userData },
    });
  }
};

// Clear error function
const clearError = (): void => {
  dispatch({ type: 'CLEAR_ERROR' });
};

// Refresh auth function
const refreshAuth = async (): Promise<void> => {
  try {
    // Server reads refresh token from HttpOnly cookie
    const response = await api.post<ApiResponse<{ token: string }>>('/auth/refresh');
    if (response.data.success && response.data.data) {
      const newToken = response.data.data.token;
      if (state.user) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: state.user, token: newToken, refreshToken: null },
        });
      }
    } else {
      throw new Error('Token refresh failed');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    logout();
  }
};

// Context value
const value: AuthContextType = {
  ...state,
  login,
  register,
  logout,
  updateUser,
  clearError,
  refreshAuth,
};

return (
  <AuthContext.Provider value={value}>
    {children}
  </AuthContext.Provider>
);
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
